<template>
  <div class="article push" :class="{collapsed: navCollapsed}">
    <loading-indicator v-if="pending"></loading-indicator>
    <div v-if="article" class="flex article-header">
      <div class="col-5 article-header-image" :style="{backgroundImage: `url(${article.header_image_url})`}"></div>
      <div class="col-7 flex items-center article-profile" :class="article.categories[0].slug">
        <div class="ml-3 article-profile-name">
          <h4 class="first-name">{{ article.first_name }}</h4>
          <h1 class="last-name uppercase">{{ article.last_name }}</h1>
        </div>
      </div>
    </div>
    <fast-facts v-if="article" :facts="article.fast_facts" :category="article.categories[0]"></fast-facts>
    <article-content v-if="article" :content="article.content" :category-slug="article.categories[0].slug"/>
    <div v-if="article && relatedArticles.length" class="related-section">
      <div class="body-8 text-white text-center related-section-title-bar" :class="article.categories[0].slug">
        You may also be interested in...
      </div>
      <div class="relative overflow-hidden w-full">
        <div class="article-carousel">
          <carousel :key="article.id"
              :loop="true"
              :items="4"
              :margin="8"
              :mouseDrag="false"
              :touchDrag="false"
              :dots="false"
              ref="carousel"
          >
            <template slot="prev">
              <carousel-navigation ref="prevNav" :direction="'prev'"/>
            </template>
            <player-profile-link
                v-for="article in relatedArticles"
                :key="article.id"
                :article="article"
                :show-article-category-icon="true"
            />
            <template slot="next">
              <carousel-navigation :direction="'next'"/>
            </template>
          </carousel>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import carousel from 'vue-owl';
  import CarouselNavigation from '@/component/CarouselNavigation';
  import FastFacts from '@/component/FastFacts';
  import PlayerProfileLink from '@/views/Student/PlayerProfileLink';
  import { CategoryLoaded } from "@/common/guards/categoryLoaded.guard";
  import { ArticleSelectedGuard } from '@/common/guards/articleSelected.guard';
  import { ArticleViewed } from '@/common/guards/articleViewed.guard';
  import ArticleContent from '@/views/Student/Article/ArticleContent';
  import LoadingIndicator from '@/component/LoadingIndicator';

  export default {
    name: 'index',

    components: {
      ArticleContent,
      FastFacts,
      CarouselNavigation,
      carousel,
      PlayerProfileLink,
      LoadingIndicator
    },

    data() {
      return {
        ADMIN_API_HOST: process.env.VUE_APP_ADMIN_HOST,
      }
    },

    computed: {
      ...mapGetters({
        articles: 'articles',
        pending: 'articlesPending',
        article: 'selectedArticle',
        relatedArticles: 'relatedArticles',
        navCollapsed: 'navCollapsed'
      })
    },

    beforeRouteUpdate(to, from, next) {
      ArticleSelectedGuard(to, from, next);
      CategoryLoaded(to, from, next);
      ArticleViewed(to, from, next);
      next();
    },
  }
</script>

<style lang="scss">
  .profile-carousel {
    margin: 0 auto;
    width: calc(100% - 45px);

    .carousel-prev,
    .carousel-next {
      @apply absolute z-10 bg-transparent;
      height: 100%;
      top: 0;
      width: 20px;
      z-index: 1;
      margin: 0 15px;
    }

    .carousel-prev {
      left: -15px;
    }

    .carousel-next {
      right: -15px;
    }

    .owl-stage-outer {
      overflow: hidden !important;
    }
  }

  .article {
    transition: margin-top .25s ease;

    &.push {
      padding-top: 307px;

      @media (min-width: config('screens.lg')) {
        padding-top: calc(config('staticHeight.header') + config('staticHeight.categoryCarousel') + config('staticHeight.breadcrumbs')
        + config('staticHeight.quickNav'));
      }
    }

    &.collapsed {
      margin-top: -214px;
    }

    .article-carousel {
      width: 980px;
      margin: 8px auto 0;
      height: 200px;
    }

    .article-header {
      height: 240px;
      margin-bottom: 24px;

      .article-profile {
        position: relative;

        .article-profile-name {
          z-index: 10px;

          .first-name {
            color: #fff;
          }

          .last-name {
            color: #fff;
            line-height: 60px;
            word-break: break-word;
          }
        }

        background-repeat: no-repeat;
        background-position: center;

        &.basketball {
          background-image: url("../../../assets/images/57-lrn-spt-prof-head-cat-1-285.svg");
          @apply bg-c1-b;
        }

        &.football {
          background-image: url("../../../assets/images/58-lrn-spt-prof-head-cat-2-398.svg");
          @apply bg-c2-b;
        }

        &.baseball {
          background-image: url("../../../assets/images/59-lrn-spt-prof-head-cat-3-254.svg");
          @apply bg-c3-b;
        }

        &.soccer {
          background-image: url("../../../assets/images/60-lrn-spt-prof-head-cat-4-285.svg");
          @apply bg-c4-b;
        }

        &.more-sports {
          background-image: url("../../../assets/images/more-sports-category-article-header.svg");
          @apply bg-c5-b;
        }

        &.olympics {
          background-image: url("../../../assets/images/olympics-category-article-header.svg");
          @apply bg-c6-b;
        }

        &.hockey {
          background-image: url("../../../assets/images/hockey-category-article-header.svg");
          @apply bg-c7-b;
        }

        &.tennis {
          background-image: url("../../../assets/images/tennis-category-article-header.svg");
          @apply bg-c8-b;
        }

        &.default {
          @apply bg-grey-1;
        }
      }

      .article-header-image {
        height: 100%;
        background-size: cover;
        background-position: center center;
        background-repeat: no-repeat;
      }
    }

    .fast-facts-header {
      height: 120px;

      .ff-logo {
        background: no-repeat url("../../../assets/images/62-lrn-spt-prof-fast-facts-350.svg");
        width: 350px;
        height: 75px;
      }

      &.basketball {
        @apply bg-c1-e;
      }

      &.football {
        @apply bg-c2-e;
      }

      &.baseball {
        @apply bg-c3-e;
      }

      &.soccer {
        @apply bg-c4-e;
      }

      &.more-sports {
        @apply bg-c5-e;
      }

      &.olympics {
        @apply bg-c6-e;
      }

      &.hockey {
        @apply bg-c7-e;
      }

      &.tennis {
        @apply bg-c8-e;
      }

      &.default {
        @apply bg-grey-1;
      }
    }

    .related-section-title-bar {
      margin-bottom: 8px;

      &.basketball {
        @apply bg-c1-g;
      }

      &.football {
        @apply bg-c2-g;
      }

      &.baseball {
        @apply bg-c3-g;
      }

      &.soccer {
        @apply bg-c4-g;
      }

      &.more-sports {
        @apply bg-c5-g;
      }

      &.olympics {
        @apply bg-c6-g;
      }

      &.hockey {
        @apply bg-c7-g;
      }

      &.tennis {
        @apply bg-c8-g;
      }

      &.default {
        @apply bg-grey-1;
      }
    }
  }
</style>
